import type { AppSettings, QuestionItem, QuestionSession } from "./types";
import { parseJsonObject } from "./json";

export interface QuestionDecision {
  status: "questions" | "sufficient";
  questions: Array<{ text: string; required: boolean }>;
  uncertainty?: string;
}

export function normalizeQuestion(text: string): string {
  return text
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseQuestionDecision(content: string): QuestionDecision {
  let parsed: Partial<QuestionDecision>;
  try {
    parsed = parseJsonObject(content) as Partial<QuestionDecision>;
  } catch {
    throw new Error("Risposta della Modalità domande non valida.");
  }
  if (parsed.status !== "questions" && parsed.status !== "sufficient") {
    throw new Error("Risposta della Modalità domande non valida.");
  }
  return {
    status: parsed.status,
    questions: Array.isArray(parsed.questions)
      ? parsed.questions
          .filter(
            (question): question is { text: string; required: boolean } =>
              Boolean(
                question &&
                  typeof question.text === "string" &&
                  question.text.trim(),
              ),
          )
          .map((question) => ({
            text: question.text.trim(),
            required: Boolean(question.required),
          }))
      : [],
    ...(typeof parsed.uncertainty === "string"
      ? { uncertainty: parsed.uncertainty }
      : {}),
  };
}

export function selectNewQuestions(
  session: QuestionSession,
  decision: QuestionDecision,
): QuestionItem[] {
  const known = new Set(session.questions.map((item) => normalizeQuestion(item.text)));
  const selected: QuestionItem[] = [];
  for (const question of decision.questions) {
    const normalized = normalizeQuestion(question.text);
    if (!normalized || known.has(normalized)) continue;
    known.add(normalized);
    selected.push({
      id: crypto.randomUUID(),
      text: question.text,
      required: question.required,
    });
  }
  return selected;
}

export function capNewQuestions(
  existingCount: number,
  maxQuestions: number,
  questions: QuestionItem[],
): QuestionItem[] {
  return questions.slice(0, Math.max(0, maxQuestions - existingCount));
}

export function buildQuestionAnalysisPrompt(
  session: QuestionSession,
  maxQuestions: number,
): string {
  const dossier = session.questions
    .map(
      (question, index) =>
        `${index + 1}. ${question.text}\nRisposta: ${
          question.skipped ? "(saltata)" : question.answer || "(in attesa)"
        }`,
    )
    .join("\n\n");
  return `Analyze which concrete facts are still missing before answering the user's original request.

Original request (preserve exactly):
${session.originalRequest}

Clarifications collected:
${dossier || "(none)"}

Return ONLY JSON:
{"status":"questions"|"sufficient","questions":[{"text":"short concrete question","required":true|false}],"uncertainty":"optional"}

Rules:
- ask only facts that materially improve the final answer;
- do not repeat or paraphrase prior questions;
- distinguish mandatory from optional information;
- avoid generic questions;
- ask at most ${maxQuestions} new questions;
- if the available information is sufficient, return status "sufficient" and an empty list.`;
}

export function formatQuestions(questions: QuestionItem[]): string {
  const required = questions.filter((question) => question.required);
  const optional = questions.filter((question) => !question.required);
  const section = (title: string, items: QuestionItem[]) =>
    items.length
      ? `### ${title}\n${items
          .map((question, index) => `${index + 1}. ${question.text}`)
          .join("\n")}`
      : "";
  return [
    "Per preparare una risposta precisa mi servono questi chiarimenti:",
    section("Informazioni necessarie", required),
    section("Informazioni facoltative", optional),
  ]
    .filter(Boolean)
    .join("\n\n");
}

export function buildQuestionDossier(session: QuestionSession): string {
  return `Richiesta originale:
${session.originalRequest}

Dossier dei chiarimenti:
${session.questions
  .map(
    (question) =>
      `- Domanda: ${question.text}\n  Risposta: ${
        question.skipped ? "(informazione saltata)" : question.answer || "(non fornita)"
      }`,
  )
  .join("\n")}

Incertezze residue:
${session.questions
  .filter((question) => question.skipped || !question.answer)
  .map((question) => `- ${question.text}`)
  .join("\n") || "(nessuna dichiarata)"}`;
}

export function shouldEndQuestionLoop(
  session: QuestionSession,
  maxRounds: number,
  maxQuestions: number,
): "continue" | "limit_reached" | "no_progress" {
  if (session.round >= maxRounds || session.questions.length >= maxQuestions) {
    return "limit_reached";
  }
  if (session.noProgressCount >= 2) return "no_progress";
  return "continue";
}

export function engineAfterQuestions(
  settings: Pick<AppSettings, "council" | "agentTeam">,
): "standard" | "council" | "agent_team" {
  if (settings.agentTeam.enabled) return "agent_team";
  if (settings.council.enabled) return "council";
  return "standard";
}
